import { v } from 'convex/values'

export const availabilityValidator = v.object({
  // 0 = Sunday … 6 = Saturday, in the coach's own timezone.
  days: v.array(v.number()),
  // "HH:mm" in the coach's timezone; both or neither.
  from: v.optional(v.string()),
  to: v.optional(v.string()),
  tz: v.string(),
})

export type Availability = {
  days: number[]
  from?: string
  to?: string
  tz: string
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

export function cleanAvailability(a: Availability): Availability | null {
  const days = [...new Set(a.days)].sort((x, y) => x - y)
  if (days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) throw new Error('Invalid days')
  try {
    new Intl.DateTimeFormat('en', { timeZone: a.tz })
  } catch {
    throw new Error('Invalid timezone')
  }
  if (!days.length) return null
  if (a.from === undefined && a.to === undefined) return { days, tz: a.tz }
  if (!a.from || !a.to || !HHMM.test(a.from) || !HHMM.test(a.to) || a.from === a.to)
    throw new Error('Invalid hours')
  return { days, from: a.from, to: a.to, tz: a.tz }
}

// Green dot: today is one of the coach's days and, if hours are set, now falls inside them.
export function isAvailableNow(a: Availability, now = Date.now()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: a.tz,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'))
  const mins = Number(get('hour')) * 60 + Number(get('minute'))
  const toMins = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3))
  if (!a.from || !a.to) return a.days.includes(day)
  const [from, to] = [toMins(a.from), toMins(a.to)]
  // An overnight window (22:00–02:00) belongs to the day it starts on.
  if (from < to) return a.days.includes(day) && mins >= from && mins < to
  if (mins >= from) return a.days.includes(day)
  return mins < to && a.days.includes((day + 6) % 7)
}

// "Mon · Wed · Fri", week starting Monday, names localized by Intl.
export function formatAvailability(a: Availability, locale: string) {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
  const days = [...a.days]
    .sort((x, y) => ((x + 6) % 7) - ((y + 6) % 7))
    .map((d) => fmt.format(Date.UTC(2023, 0, 1 + d)))
    .join(' · ')
  return a.from && a.to ? `${days} · ${a.from}–${a.to}` : days
}
